/* Native modal and horizontal scrolling; gallery links work without JavaScript. */
if (!customElements.get('objectiv-product-gallery')) {
  customElements.define('objectiv-product-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.lifetime) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal) return;
      this.lifetime = new AbortController();
      const options = { signal: this.lifetime.signal };
      this.mobile = matchMedia('(max-width: 749px), (max-width: 989px) and (max-height: 500px)');
      this.arrange();
      this.lightSources ||= new WeakMap();
      this.themedLinks = this.links.filter(link => link.hasAttribute('data-gallery-dark-src'));
      this.themedLinks.forEach(link => {
        if (this.lightSources.has(link)) return;
        const image = link.querySelector('img');
        this.lightSources.set(link, {href:link.getAttribute('href'),src:image.getAttribute('src'),srcset:image.getAttribute('srcset'),width:image.getAttribute('width'),height:image.getAttribute('height')});
      });
      this.track = this.querySelector('[data-gallery-track]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.previous = this.querySelector('[data-gallery-step="-1"]');
      this.next = this.querySelector('[data-gallery-step="1"]');
      this.closeButton = this.querySelector('[data-gallery-close]');
      this.index = 0;
      this.galleryMode = null;
      this.syncPalette();
      document.addEventListener('objectiv:color-mode-change', () => this.syncPalette(), options);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inline = this.querySelector('[data-inline-gallery-track]');
      this.inlineControls = this.querySelector('[data-inline-gallery-controls]');
      this.inlinePrevious = this.querySelector('[data-inline-gallery-step="-1"]');
      this.inlineNext = this.querySelector('[data-inline-gallery-step="1"]');
      this.inlineCounter = this.querySelector('[data-inline-gallery-counter]');
      this.inlineIndex = 0;
      if (this.inline) {
        this.inlineControls.hidden = this.links.length < 2;
        this.inlinePrevious.addEventListener('click', () => this.selectInline(this.inlineIndex - 1), options);
        this.inlineNext.addEventListener('click', () => this.selectInline(this.inlineIndex + 1), options);
        this.inline.addEventListener('keydown', event => {
          const targets = {ArrowLeft:this.inlineIndex - 1,ArrowRight:this.inlineIndex + 1,Home:0,End:this.links.length - 1};
          if (!(event.key in targets) || !this.mobile.matches) return;
          event.preventDefault(); this.selectInline(targets[event.key]);
        }, options);
        this.inline.addEventListener('scroll', () => {
          if (this.inlineFrame) return;
          this.inlineFrame = requestAnimationFrame(() => {
            this.inlineFrame = 0;
            if (!this.inline.clientWidth || !this.mobile.matches) return;
            const index = Math.round(this.inline.scrollLeft / this.inline.clientWidth);
            if (this.inlinePending != null && index !== this.inlinePending) return;
            this.inlinePending = null; this.updateInline(index);
          });
        }, {...options,passive:true});
        this.inline.addEventListener('scrollend', () => {
          this.inlinePending = null;
          if (this.inline.clientWidth && this.mobile.matches) this.updateInline(Math.round(this.inline.scrollLeft / this.inline.clientWidth));
        }, options);
        for (const type of ['pointerdown','wheel']) this.inline.addEventListener(type, () => { this.inlinePending = null; }, {...options,passive:true});
        this.mobile.addEventListener('change', () => { this.arrange(); this.selectInline(this.inlineIndex, false); }, options);
        this.updateInline(0);
      }
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-gallery-image]');
        if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 ||
            event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(link);
      }, options);
      this.closeButton.addEventListener('click', () => this.dialog.close(), options);
      this.previous.addEventListener('click', () => this.select(this.index - 1), options);
      this.next.addEventListener('click', () => this.select(this.index + 1), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        const targets = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.links.length - 1 };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this.select(targets[event.key]);
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow;
        if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      }, options);
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (!this.dialog.open || !this.track.clientWidth) return;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          // Keep the requested image selected while a smooth scroll passes other slides.
          if (this.pendingIndex != null && index !== this.pendingIndex) return;
          this.pendingIndex = null;
          this.update(index);
        });
      }, { ...options, passive: true });
      this.track.addEventListener('scrollend', () => {
        this.pendingIndex = null;
        if (this.dialog.open && this.track.clientWidth) this.update(Math.round(this.track.scrollLeft / this.track.clientWidth));
      }, options);
      for (const type of ['pointerdown', 'wheel']) {
        this.track.addEventListener(type, () => { this.pendingIndex = null; }, { ...options, passive: true });
      }
      this.resize = new ResizeObserver(() => {
        if (this.dialog.open) this.select(this.index, false);
        const width = this.inline?.clientWidth || 0;
        // Intrinsic/lazy image heights can change during a native swipe.
        // Only a new slide width requires realigning the horizontal position.
        if (width !== this.inlineWidth) {
          this.inlineWidth = width;
          if (width && this.mobile.matches) this.selectInline(this.inlineIndex, false);
        }
      });
      this.resize.observe(this.track);
      if (this.inline) this.resize.observe(this.inline);
      this.motion.addEventListener('change', () => {
        if (this.dialog.open) this.select(this.index, false);
        if (this.inline?.clientWidth && this.mobile.matches) this.selectInline(this.inlineIndex, false);
      }, options);
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow;
      }
      this.lifetime?.abort();
      this.lifetime = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.inlineFrame);
      this.frame = 0;
      this.inlineFrame = 0;
    }

    updateInline(index) {
      this.inlineIndex = Math.max(0, Math.min(this.links.length - 1, index));
      this.inlinePrevious.disabled = this.inlineIndex === 0;
      this.inlineNext.disabled = this.inlineIndex === this.links.length - 1;
      this.inlineCounter.textContent = `${this.inlineIndex + 1} / ${this.links.length}`;
      this.dataset.inlineIndex = String(this.inlineIndex);
      this.links.forEach((link, number) => {
        if (this.mobile.matches) link.tabIndex = number === this.inlineIndex ? 0 : -1;
        else link.removeAttribute('tabindex');
      });
      this.inline.tabIndex = this.mobile.matches ? 0 : -1;
    }

    syncPalette() {
      const mode = document.documentElement.dataset.colorMode;
      if (this.galleryMode === mode || !this.themedLinks.length) return;
      this.galleryMode = mode;
      this.themedLinks.forEach(link => {
        const source = mode === 'dark' ? {href:link.dataset.galleryDarkSrc,src:link.dataset.galleryDarkSrc,srcset:link.dataset.galleryDarkSrcset,width:link.dataset.galleryDarkWidth,height:link.dataset.galleryDarkHeight} : this.lightSources.get(link);
        const image = link.querySelector('img');
        image.srcset = source.srcset;
        image.src = source.src;
        image.width = Number(source.width);
        image.height = Number(source.height);
        link.href = source.href;
      });
      [...this.track.children].forEach((slide, index) => {
        const source = this.links[index].querySelector('img'), image = slide.querySelector('img');
        image.dataset.src = this.links[index].href;
        image.dataset.srcset = source.getAttribute('srcset') || '';
        image.width = Number(source.getAttribute('width'));
        image.height = Number(source.getAttribute('height'));
        if (image.hasAttribute('src')) {
          image.srcset = image.dataset.srcset;
          image.src = image.dataset.src;
        }
      });
    }

    // Phones show the fourth image second; larger layouts keep the authored order.
    arrange() {
      this.sourceOrder ||= [...this.querySelectorAll('[data-gallery-image]')];
      const items = this.sourceOrder, order = this.mobile.matches && items.length > 3
        ? [items[0], items[3], ...items.slice(1, 3), ...items.slice(4)] : items;
      order.forEach(item => item.parentElement.append(item));
      this.links = order;
      this.track?.replaceChildren();
    }

    selectInline(index, animate = true) {
      if (!this.inline) return;
      this.updateInline(index);
      if (!this.mobile.matches) return;
      const smooth = animate && !this.motion.matches;
      this.inlinePending = smooth ? this.inlineIndex : null;
      this.inline.scrollTo({left:this.inlineIndex * this.inline.clientWidth,behavior:smooth?'smooth':'instant'});
    }

    open(link) {
      if (!this.track.children.length) {
        this.links.forEach(anchor => {
          const source = anchor.querySelector('img');
          const slide = document.createElement('div');
          slide.className = 'obj-product-gallery__slide';
          const image = document.createElement('img');
          image.alt = source.alt;
          image.width = Number(source.getAttribute('width'));
          image.height = Number(source.getAttribute('height'));
          image.dataset.src = anchor.href;
          image.dataset.srcset = source.getAttribute('srcset') || '';
          image.decoding = 'async';
          slide.append(image);
          this.track.append(slide);
        });
      }
      this.trigger = link;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.select(this.links.indexOf(link), false);
      this.closeButton.focus({ preventScroll: true });
    }

    select(index, animate = true) {
      this.update(index);
      const smooth = animate && !this.motion.matches;
      this.pendingIndex = smooth ? this.index : null;
      this.track.scrollTo({ left: this.index * this.track.clientWidth,
        behavior: smooth ? 'smooth' : 'instant' });
    }

    update(index) {
      this.index = Math.max(0, Math.min(this.links.length - 1, index));
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.links.length - 1;
      this.counter.textContent = `${this.index + 1} / ${this.links.length}`;
      [...this.track.children].forEach((slide, number) => {
        slide.setAttribute('aria-hidden', String(number !== this.index));
        const image = slide.querySelector('img');
        if (Math.abs(number - this.index) <= 1) {
          image.sizes = `${Math.min(this.track.clientWidth, this.track.clientHeight)}px`;
          if (!image.hasAttribute('src')) {
            if (image.dataset.srcset) image.srcset = image.dataset.srcset;
            image.src = image.dataset.src;
          }
        } else {
          image.removeAttribute('src');
          image.removeAttribute('srcset');
        }
      });
    }
  });
}
